/**
 * Trust Centre settings — where an administrator decides what the world sees.
 *
 * THE ONE IDEA ON THIS SCREEN: publishing is an act, not a default.
 *   * The page starts OFF and nothing is ticked.
 *   * Each framework and each policy is ticked individually; there is no
 *     "select all" and no "publish everything".
 *   * The list of what is NEVER published is on the screen, in words, next to the
 *     switch — so the administrator is told what they cannot expose as well as
 *     what they can.
 *   * Turning the page on asks for one more explicit confirmation.
 *   * The preview is the real public view (`TrustCentreView`, assembled by the
 *     same server code the public endpoint uses), not a mock-up.
 *
 * The server enforces all of this: the administrator-only rule, the ban on
 * external members changing it, the ownership of every selected id. This screen
 * hides what the user cannot do as a courtesy; it controls nothing.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardHeader, Button, Modal, Field, Pill, Skeleton, ErrorNotice, type Tone } from '../components/ui';
import { IconShield } from '../components/icons';
import { cn } from '../lib/cn';
import { TrustCentreView, useTrustStrings, TRUST_STRINGS, type PublicTrustView, type TrustStringKey } from './TrustCentre';
import { useTrustPortalT } from '../lib/trustPortalStrings';
import { TrustAnswersTab } from './trust/TrustAnswersTab';
import { TrustAccessTab } from './trust/TrustAccessTab';

/**
 * Trust Centre admin — a tabbed shell over three screens:
 *   * Public page    (capability `org.update`)  — the existing publish screen.
 *   * Answers        (capability `trust.manage`) — author the reverse-replies.
 *   * External access(capability `trust.manage`) — invite/triage external users.
 *
 * The tabs a user sees follow their capabilities: an ADMINISTRATOR holds both
 * and sees all three; a SECURITY user holds `trust.manage` but not `org.update`
 * and sees Answers + External access only. The server enforces each capability
 * independently — this only decides what to show.
 */
type TrustTab = 'publish' | 'answers' | 'access';

export function TrustCentreSettingsPage() {
  const { allows } = useAuth();
  const { tx: tp } = useTrustPortalT();
  const { tx } = useTrustStrings();

  const canPublish = allows('org.update');
  const canManage = allows('trust.manage');

  // Shared with PublishTab via the react-query cache; here only for the portal
  // link shown on the External access tab.
  const { data: settings } = useQuery({
    queryKey: ['trust-centre-settings'],
    queryFn: () => api.get<Settings>('/trust-centre'),
    enabled: canPublish,
  });

  const tabs: Array<{ id: TrustTab; label: string; show: boolean }> = [
    { id: 'publish', label: tp('trust.tab.publish'), show: canPublish },
    { id: 'answers', label: tp('trust.tab.answers'), show: canManage },
    { id: 'access', label: tp('trust.tab.access'), show: canManage },
  ];
  const available = tabs.filter((t) => t.show);
  const [tab, setTab] = useState<TrustTab>(() => available[0]?.id ?? 'publish');

  if (available.length === 0) {
    return (
      <div className="oc-page py-6">
        <h1 className="oc-h1">{tx('trust.settings.title')}</h1>
        <div className="mt-4"><ErrorNotice message={tx('trust.settings.adminOnly')} /></div>
      </div>
    );
  }

  return (
    <div className="oc-page py-4 sm:py-6 space-y-5">
      <h1 className="oc-h1">{tx('trust.settings.title')}</h1>
      <div className="flex flex-wrap gap-1 border-b border-line" role="tablist">
        {available.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors',
              tab === t.id ? 'border-brand text-ink' : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'publish' && canPublish && <PublishTab />}
      {tab === 'answers' && canManage && <TrustAnswersTab />}
      {tab === 'access' && canManage && <TrustAccessTab slug={settings?.slug ?? null} />}
    </div>
  );
}

interface Settings {
  configured: boolean;
  enabled: boolean;
  slug: string | null;
  organizationName: string;
  displayName: string | null;
  description: string | null;
  contactEmail: string | null;
  frameworkIds: string[];
  policyIds: string[];
  showExternalScan: boolean;
  options: {
    frameworks: Array<{
      id: string; name: string; version: string;
      status: 'FINALIZED' | 'IN_PROGRESS';
      compliancePercent: number | null; finalizedAt: string | null;
    }>;
    policies: Array<{ id: string; name: string; approvalStatus: 'APPROVED' | 'IN_REVIEW' | 'NOT_APPROVED' }>;
    externalScan: { lastCompletedAt: string | null };
  };
  preview: PublicTrustView;
}

/** The part of the settings the form edits. */
interface Draft {
  enabled: boolean;
  displayName: string;
  description: string;
  contactEmail: string;
  frameworkIds: string[];
  policyIds: string[];
  showExternalScan: boolean;
}

const DESCRIPTION_MAX = 600;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const POLICY_TONE: Record<'APPROVED' | 'IN_REVIEW' | 'NOT_APPROVED', Tone> = {
  APPROVED: 'good', IN_REVIEW: 'warning', NOT_APPROVED: 'neutral',
};

function toDraft(s: Settings): Draft {
  return {
    enabled: s.enabled,
    displayName: s.displayName ?? '',
    description: s.description ?? '',
    contactEmail: s.contactEmail ?? '',
    frameworkIds: s.frameworkIds,
    policyIds: s.policyIds,
    showExternalScan: s.showExternalScan,
  };
}

/** Same set regardless of order. */
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

function isDirty(a: Draft, b: Draft) {
  return a.enabled !== b.enabled || a.displayName.trim() !== b.displayName.trim()
    || a.description.trim() !== b.description.trim()
    || a.contactEmail.trim().toLowerCase() !== b.contactEmail.trim().toLowerCase()
    || !sameSet(a.frameworkIds, b.frameworkIds) || !sameSet(a.policyIds, b.policyIds)
    || a.showExternalScan !== b.showExternalScan;
}

/** The existing "what the public sees" screen, now the Public page tab. */
function PublishTab() {
  const { tx, formatDate } = useTrustStrings();
  const { session, allows } = useAuth();
  const queryClient = useQueryClient();

  const canManage = allows('org.update');
  const isExternal = Boolean(
    session?.organizations.find((o) => o.id === session.organization.id)?.isExternal,
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['trust-centre-settings'],
    queryFn: () => api.get<Settings>('/trust-centre'),
    enabled: canManage,
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [copied, setCopied] = useState<'ok' | 'failed' | null>(null);

  // Seed the form from the server whenever a fresh copy arrives (first load,
  // after a save, after a link rotation).
  // Not after a link rotation, though: that changes only the link, and must not
  // silently throw away what the administrator has typed but not yet saved.
  const keepDraft = useRef(false);
  useEffect(() => {
    if (!data) return;
    if (keepDraft.current) {
      keepDraft.current = false;
      return;
    }
    setDraft(toDraft(data));
  }, [data]);

  const saved = useMemo(() => (data ? toDraft(data) : null), [data]);
  const dirty = draft && saved ? isDirty(draft, saved) : false;

  const describe = (err: unknown): string => {
    if (err instanceof ApiError) {
      const key = `trust.settings.error.${err.code}` as TrustStringKey;
      return (key as string) in TRUST_STRINGS ? tx(key) : tx('trust.settings.error.UNKNOWN');
    }
    return tx('trust.settings.error.UNKNOWN');
  };

  const save = useMutation({
    mutationFn: (d: Draft) =>
      api.put<Settings>('/trust-centre', {
        enabled: d.enabled,
        displayName: d.displayName.trim() || null,
        description: d.description.trim() || null,
        contactEmail: d.contactEmail.trim() || null,
        frameworkIds: d.frameworkIds,
        policyIds: d.policyIds,
        showExternalScan: d.showExternalScan,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(['trust-centre-settings'], next);
      setMessage({ kind: 'ok', text: tx('trust.settings.saved') });
    },
    onError: (err) => setMessage({ kind: 'error', text: describe(err) }),
  });

  const rotate = useMutation({
    mutationFn: () => api.post<Settings>('/trust-centre/rotate-link', {}),
    onSuccess: (next) => {
      keepDraft.current = true;
      queryClient.setQueryData(['trust-centre-settings'], next);
      setConfirmRotate(false);
      setCopied(null);
      setMessage({ kind: 'ok', text: tx('trust.settings.saved') });
    },
    onError: (err) => {
      setConfirmRotate(false);
      setMessage({ kind: 'error', text: describe(err) });
    },
  });

  const emailBad = draft !== null && draft.contactEmail.trim() !== '' && !EMAIL_SHAPE.test(draft.contactEmail.trim());

  const update = (patch: Partial<Draft>) => {
    setMessage(null);
    setDraft((d) => (d ? { ...d, ...patch } : d));
  };
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  /** Switching ON needs a second, explicit yes. Everything else saves directly. */
  const requestSave = () => {
    if (!draft || emailBad) return;
    if (draft.enabled && !data?.enabled) setConfirmPublish(true);
    else save.mutate(draft);
  };

  const publicUrl = data?.slug ? `${window.location.origin}/trust/${data.slug}` : null;

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
  };

  // ---- Not allowed ----------------------------------------------------------
  if (!canManage) {
    return <ErrorNotice message={tx('trust.settings.adminOnly')} />;
  }

  return (
    <div className="space-y-5">
      <p className="oc-muted max-w-2xl">{tx('trust.settings.subtitle')}</p>

      {isLoading && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-28" /><Skeleton className="h-64" />
        </div>
      )}
      {isError && <ErrorNotice message={tx('trust.settings.loadFailed')} onRetry={() => void refetch()} retryLabel={tx('trust.public.retry')} />}

      {data && draft && (
        <>
          {isExternal && <ErrorNotice message={tx('trust.settings.externalReadOnly')} />}

          {/* What is public, and what never is — before any control. */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-warning/40 bg-warning/[0.08] px-4 py-3">
              <p className="font-medium text-ink">{tx('trust.settings.warningTitle')}</p>
              <p className="text-sm text-ink-secondary mt-1">{tx('trust.settings.warningBody')}</p>
            </div>
            <div className="rounded-xl border border-line bg-surface-raised px-4 py-3">
              <p className="font-medium text-ink">{tx('trust.settings.neverTitle')}</p>
              <p className="text-sm text-ink-secondary mt-1">{tx('trust.settings.neverBody')}</p>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-2 items-start">
            {/* ------------------------------ form ------------------------------ */}
            <div className="space-y-4 min-w-0">
              <Card>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <label htmlFor="trust-publish" className="font-medium text-ink">{tx('trust.settings.publishLabel')}</label>
                    <p className="text-sm text-ink-secondary mt-0.5">{tx('trust.settings.publishHint')}</p>
                  </div>
                  <input
                    id="trust-publish"
                    type="checkbox"
                    role="switch"
                    aria-checked={draft.enabled}
                    checked={draft.enabled}
                    disabled={isExternal}
                    onChange={(e) => update({ enabled: e.target.checked })}
                    className="mt-1 h-6 w-11 shrink-0 appearance-none rounded-full bg-ink/20 checked:bg-brand relative cursor-pointer transition-colors
                               before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:h-5 before:w-5 before:rounded-full before:bg-white before:transition-transform
                               checked:before:translate-x-5 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Pill tone={data.enabled ? 'good' : 'neutral'}>
                    {data.enabled ? tx('trust.settings.statePublished') : tx('trust.settings.stateNotPublished')}
                  </Pill>
                  {dirty && <Pill tone="warning">{tx('trust.settings.unsaved')}</Pill>}
                </div>

                <div className="mt-4 pt-4 border-t border-line">
                  <p className="oc-label">{tx('trust.settings.linkTitle')}</p>
                  {publicUrl ? (
                    <>
                      <input
                        readOnly
                        value={publicUrl}
                        aria-label={tx('trust.settings.linkTitle')}
                        onFocus={(e) => e.currentTarget.select()}
                        className="oc-input font-mono text-xs sm:text-xs"
                      />
                      {!data.enabled && <p className="mt-1.5 text-xs text-ink-muted">{tx('trust.settings.linkOffNote')}</p>}
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => void copyLink()}>
                          {copied === 'ok' ? tx('trust.settings.copied') : tx('trust.settings.copy')}
                        </Button>
                        <a className="oc-btn-secondary oc-btn-sm" href={publicUrl} target="_blank" rel="noopener noreferrer">
                          {tx('trust.settings.open')}
                        </a>
                        {!isExternal && (
                          <Button size="sm" variant="ghost" onClick={() => setConfirmRotate(true)}>{tx('trust.settings.rotate')}</Button>
                        )}
                      </div>
                      <p role="status" className="mt-1.5 text-xs text-ink-muted min-h-[1rem]">
                        {copied === 'failed' ? tx('trust.settings.copyFailed') : ''}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-ink-muted">{tx('trust.settings.linkNone')}</p>
                  )}
                </div>
              </Card>

              <Card>
                <CardHeader title={tx('trust.settings.aboutTitle')} />
                <Field label={tx('trust.settings.displayName')} hint={tx('trust.settings.displayNameHint', { name: data.organizationName })} htmlFor="trust-name">
                  <input id="trust-name" className="oc-input" maxLength={120} value={draft.displayName} disabled={isExternal}
                    onChange={(e) => update({ displayName: e.target.value })} />
                </Field>
                <Field
                  label={tx('trust.settings.description')}
                  hint={`${tx('trust.settings.descriptionHint')} ${tx('trust.settings.descriptionCount', { used: draft.description.length, max: DESCRIPTION_MAX })}`}
                  htmlFor="trust-description"
                >
                  <textarea id="trust-description" className="oc-input py-2 min-h-[6rem]" rows={4} maxLength={DESCRIPTION_MAX}
                    value={draft.description} disabled={isExternal}
                    onChange={(e) => update({ description: e.target.value })} />
                </Field>
                <Field label={tx('trust.settings.contact')} hint={tx('trust.settings.contactHint')}
                  error={emailBad ? tx('trust.settings.contactBad') : undefined} htmlFor="trust-contact">
                  <input id="trust-contact" type="email" inputMode="email" autoComplete="off" className="oc-input" maxLength={320}
                    value={draft.contactEmail} disabled={isExternal}
                    onChange={(e) => update({ contactEmail: e.target.value })} />
                </Field>
              </Card>

              <Card>
                <CardHeader
                  title={tx('trust.settings.frameworksTitle')}
                  subtitle={tx('trust.settings.frameworksHint')}
                  action={<span className="text-xs text-ink-muted" aria-live="polite">{tx('trust.settings.frameworksSelected', { count: draft.frameworkIds.length })}</span>}
                />
                {data.options.frameworks.length === 0 ? (
                  <p className="text-sm text-ink-muted">{tx('trust.settings.frameworksNone')}</p>
                ) : (
                  <ul className="space-y-2">
                    {data.options.frameworks.map((f) => (
                      <li key={f.id}>
                        <label className="flex items-start gap-3 rounded-xl border border-line p-3 min-h-[2.75rem] cursor-pointer hover:bg-surface-sunken">
                          <input type="checkbox" className="mt-1 h-4 w-4 accent-[rgb(var(--brand))]" disabled={isExternal}
                            checked={draft.frameworkIds.includes(f.id)}
                            onChange={() => update({ frameworkIds: toggle(draft.frameworkIds, f.id) })} />
                          <span className="min-w-0">
                            <span className="block font-medium text-ink break-words">{f.name}</span>
                            <span className="block text-xs text-ink-secondary">
                              {f.status === 'FINALIZED' && f.compliancePercent !== null
                                ? tx('trust.settings.frameworkFinalised', { percent: f.compliancePercent, date: formatDate(f.finalizedAt) })
                                : tx('trust.settings.frameworkInProgress')}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card>
                <CardHeader
                  title={tx('trust.settings.policiesTitle')}
                  subtitle={tx('trust.settings.policiesHint')}
                  action={<span className="text-xs text-ink-muted" aria-live="polite">{tx('trust.settings.policiesSelected', { count: draft.policyIds.length })}</span>}
                />
                {data.options.policies.length === 0 ? (
                  <p className="text-sm text-ink-muted">{tx('trust.settings.policiesNone')}</p>
                ) : (
                  <ul className="space-y-2">
                    {data.options.policies.map((p) => (
                      <li key={p.id}>
                        <label className="flex items-center gap-3 rounded-xl border border-line p-3 min-h-[2.75rem] cursor-pointer hover:bg-surface-sunken">
                          <input type="checkbox" className="h-4 w-4 shrink-0 accent-[rgb(var(--brand))]" disabled={isExternal}
                            checked={draft.policyIds.includes(p.id)}
                            onChange={() => update({ policyIds: toggle(draft.policyIds, p.id) })} />
                          <span className="min-w-0 flex-1 break-words text-ink">{p.name}</span>
                          <Pill tone={POLICY_TONE[p.approvalStatus]}>
                            {p.approvalStatus === 'APPROVED' ? tx('trust.public.policyApproved')
                              : p.approvalStatus === 'IN_REVIEW' ? tx('trust.public.policyInReview')
                              : tx('trust.public.policyNotApproved')}
                          </Pill>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card>
                <CardHeader title={tx('trust.settings.scanTitle')} />
                <label className="flex items-start gap-3 min-h-[2.75rem] cursor-pointer">
                  <input type="checkbox" className="mt-1 h-4 w-4 accent-[rgb(var(--brand))]"
                    disabled={isExternal || (!data.options.externalScan.lastCompletedAt && !draft.showExternalScan)}
                    checked={draft.showExternalScan}
                    onChange={(e) => update({ showExternalScan: e.target.checked })} />
                  <span className="min-w-0">
                    <span className="block text-ink">{tx('trust.settings.scanLabel')}</span>
                    <span className="block text-xs text-ink-secondary">{tx('trust.settings.scanHint')}</span>
                    <span className="block text-xs text-ink-muted mt-1">
                      {data.options.externalScan.lastCompletedAt
                        ? tx('trust.settings.scanLast', { date: formatDate(data.options.externalScan.lastCompletedAt) })
                        : tx('trust.settings.scanNone')}
                    </span>
                  </span>
                </label>
              </Card>

              <div className="flex flex-wrap items-center gap-3">
                <Button variant="primary" onClick={requestSave} disabled={isExternal || !dirty || emailBad || save.isPending}>
                  {save.isPending ? tx('trust.settings.saving') : tx('trust.settings.save')}
                </Button>
                <p role="status" className={message?.kind === 'error' ? 'text-sm text-critical' : 'text-sm text-good'}>
                  {message?.text ?? ''}
                </p>
              </div>
            </div>

            {/* ----------------------------- preview ---------------------------- */}
            <aside className="min-w-0 lg:sticky lg:top-4" aria-label={tx('trust.settings.previewTitle')}>
              <div className="flex items-center gap-2 mb-2">
                <IconShield className="w-5 h-5 text-ink-muted" />
                <h2 className="oc-h2">{tx('trust.settings.previewTitle')}</h2>
              </div>
              <p className="oc-muted mb-3">{tx('trust.settings.previewHint')}</p>
              {dirty && <p className="mb-3 text-sm text-warning" role="status">{tx('trust.settings.previewStale')}</p>}
              {!data.enabled && <p className="mb-3 text-sm text-ink-secondary">{tx('trust.settings.previewOff')}</p>}
              <div className="rounded-2xl border border-dashed border-line p-2 sm:p-3 bg-surface-sunken">
                <TrustCentreView view={data.preview} />
              </div>
            </aside>
          </div>
        </>
      )}

      <Modal
        open={confirmPublish}
        onClose={() => setConfirmPublish(false)}
        title={tx('trust.settings.confirmTitle')}
        footer={
          <>
            <Button onClick={() => setConfirmPublish(false)}>{tx('trust.settings.cancel')}</Button>
            <Button variant="primary" disabled={save.isPending}
              onClick={() => { setConfirmPublish(false); if (draft) save.mutate(draft); }}>
              {tx('trust.settings.confirmPublish')}
            </Button>
          </>
        }
      >
        <p className="text-ink-secondary">{tx('trust.settings.confirmBody')}</p>
      </Modal>

      <Modal
        open={confirmRotate}
        onClose={() => setConfirmRotate(false)}
        title={tx('trust.settings.rotateTitle')}
        footer={
          <>
            <Button onClick={() => setConfirmRotate(false)}>{tx('trust.settings.cancel')}</Button>
            <Button variant="danger" disabled={rotate.isPending} onClick={() => rotate.mutate()}>
              {tx('trust.settings.rotateConfirm')}
            </Button>
          </>
        }
      >
        <p className="text-ink-secondary">{tx('trust.settings.rotateBody')}</p>
      </Modal>
    </div>
  );
}
